import { useCallback, useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useDebounce } from '@uidotdev/usehooks';
import { styled } from '@mui/material/styles';
import { Box, Paper } from '@mui/material';
import {
  GridCallbackDetails,
  GridSortModel,
  GridPaginationModel,
} from '@mui/x-data-grid-pro';
import {
  DataGridHeader,
  DataGridTitle,
  DataGridFilters,
  DataGridSearch,
} from '../../../tables/DataGridPro';

import { SortModel } from '../../../../data/models/sort-model.model';

import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';

import { TripLegEventType } from '../../../../services/webSocketService/updateTripLeg';

import realtimeUtils from '../../../../services/webSocketService/realtimeUtils';

import {
  fetchTripManagementTodaysTripsAsync,
  selectAllTripLegs,
  selectTripManagementTodaysTrips,
  selectLoadingTripManagementTodaysTrips,
  selectLoadedTripManagementTodaysTrips,
  selectTripLegEventQueue,
  removeTripLegEventFromQueue,
} from '../../../../data/slices/trip-leg.slice';

import { TodaysTripsTable } from './TodaysTripsTable';
import SelectMenu from '../../../forms/SelectMenu';
import {
  TripLegException,
  TripLegExceptionBC,
} from '../../../../data/models/trip-leg-exception.model';

const PREFIX = 'TodaysTrips';

const classes = {
  container: `${PREFIX}-container`,
  tableContainer: `${PREFIX}-tableContainer`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingTop: '24px',
    paddingRight: '32px',
    paddingLeft: '32px',
  },
  [`& .${classes.tableContainer}`]: {
    width: '100%',
    maxHeight: '677px',
    height: '677px',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const DEFAULT_SORT_MODEL: SortModel = {
  column: 'isEscalated,pickUpDueTimeUTC',
  direction: 'desc,asc',
};

const TodaysTrips = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const { control, watch } = useForm({
    mode: 'onTouched',
  });
  const tripLegExceptionFilter = watch('tripLegExceptionFilter');

  const tripLegExceptions: Partial<TripLegException>[] = [
    {
      tripLegExceptionBC: 'all',
      tripLegExceptionName: t('todaysTrips.allExceptions'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.OpenSameDay,
      tripLegExceptionName: t('tripLegException.open'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.DriverLate,
      tripLegExceptionName: t('tripLegException.driverLate'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.NoDriver,
      tripLegExceptionName: t('tripLegException.noDriver'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.DriverNotOnline,
      tripLegExceptionName: t('tripLegException.driverNotOnline'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.EtaNotUpdating,
      tripLegExceptionName: t('tripLegException.etaNotUpdating'),
    },
  ];

  const [refetchTripLegs, setRefetchTripLegs] = useState(false);
  const debouncedRefetchTripLegs = useDebounce(refetchTripLegs, 200);

  const allTripLegs = useAppSelector(selectAllTripLegs);
  const tripManagementTodaysTripLegs = useAppSelector(
    selectTripManagementTodaysTrips,
  );
  const loadingTripManagementTodaysTrips = useAppSelector(
    selectLoadingTripManagementTodaysTrips,
  );
  const loadedTripManagementTodaysTrips = useAppSelector(
    selectLoadedTripManagementTodaysTrips,
  );
  const tripLegEventQueue = useAppSelector(selectTripLegEventQueue);

  const [page, setPage] = useState(1);
  // Deviate from standard page size for this page specifically F1V2-3749
  const [pageSize, setPageSize] = useState<number>(100);
  const [sortModel, setSortModel] = useState<SortModel>(DEFAULT_SORT_MODEL);

  const fetchTripLegs = useCallback(() => {
    const today = DateTime.now()

    dispatch(
      fetchTripManagementTodaysTripsAsync({
        page,
        pageSize,
        sortModel,
        pickupStartDate: today.startOf('day').toUTC().toISO(),
        pickupEndDate: today
          .plus({ days: 1 })
          .startOf('day')
          .toUTC()
          .toISO(),
        tripLegExceptionBC:
          tripLegExceptionFilter !== tripLegExceptions[0]?.tripLegExceptionBC
            ? [tripLegExceptionFilter]
            : [
                TripLegExceptionBC.DriverLate,
                TripLegExceptionBC.OpenSameDay,
                TripLegExceptionBC.NoDriver,
                TripLegExceptionBC.DriverNotOnline,
                TripLegExceptionBC.EtaNotUpdating,
              ],
        excludeRideshareWillCalls: true,
        search: searchTerm || undefined,
        isException: true,
        isUnderDiscussion: false,
        includeExceptionLog: true,
      }),
    );
  }, [dispatch, page, pageSize, sortModel, searchTerm, tripLegExceptionFilter]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handlePaginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== tripManagementTodaysTripLegs.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  const handleSortModelChange = (
    model: GridSortModel,
    _details: GridCallbackDetails,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
    } else {
      newModel = DEFAULT_SORT_MODEL;
    }
    setPage(1);
    if (newModel) {
      setSortModel(newModel);
    }
  };

  // General refetch of page for pagination, sort, search, and filter changes.
  useEffect(() => {
    setRefetchTripLegs(true);
  }, [
    fetchTripLegs,
    page,
    pageSize,
    sortModel,
    searchTerm,
    tripLegExceptionFilter,
  ]);

  // We want to listen for changes to the allTripLegs adapter here, not the tripLegEventQueue
  // because it will update before the entity is updated.
  useEffect(() => {
    if (allTripLegs.length > 0 && tripLegEventQueue.length > 0) {
      const shouldRefresh = realtimeUtils.shouldRefreshTripLegData({
        tripLegEventType: tripLegEventQueue[tripLegEventQueue.length - 1]
          .detailType as TripLegEventType,
        tripLegEvent: tripLegEventQueue[tripLegEventQueue.length - 1].detail,
        tripLegs: tripManagementTodaysTripLegs.items,
        allTripLegs,
        filters: tripManagementTodaysTripLegs.filters,
      });
      if (shouldRefresh) {
        setRefetchTripLegs(true);
      }
      dispatch(
        removeTripLegEventFromQueue({
          detailType: tripLegEventQueue[tripLegEventQueue.length - 1]
            .detailType as TripLegEventType,
          detail: tripLegEventQueue[tripLegEventQueue.length - 1].detail,
        }),
      );
    }
  }, [allTripLegs, fetchTripLegs, dispatch]);

  useEffect(() => {
    if (debouncedRefetchTripLegs) {
      fetchTripLegs();
      setRefetchTripLegs(false);
    }
  }, [debouncedRefetchTripLegs]);

  return (
    <>
      <StyledBox elevation={2}>
        <Box className={classes.container}>
          <DataGridHeader>
            <DataGridTitle>{t('todaysTrips.todaysTrips')}</DataGridTitle>

            <DataGridFilters>
              <DataGridSearch
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                placeholder={t('todaysTrips.searchPlaceholder')}
              />
              <SelectMenu
                label={t('todaysTrips.exception')}
                name="tripLegExceptionFilter"
                values={tripLegExceptions}
                valueAttr="tripLegExceptionBC"
                labelAttr="tripLegExceptionName"
                control={control}
                defaultValue={tripLegExceptions[0]?.tripLegExceptionBC}
              />
            </DataGridFilters>
          </DataGridHeader>
          <Box className={classes.tableContainer}>
            <TodaysTripsTable
              todaysTripLegs={tripManagementTodaysTripLegs}
              todaysTripLegsLoading={loadingTripManagementTodaysTrips}
              page={page}
              pageSize={pageSize}
              handlePaginationChange={handlePaginationChange}
              handleSortModelChange={handleSortModelChange}
            />
          </Box>
        </Box>
      </StyledBox>
      <Box sx={{ height: 24 }} />
    </>
  );
};

export { TodaysTrips };
